/**
 * rateSavedRooms.js — the graded rooms this browser has kept.
 *
 * 🔴 WHY IT EXISTS. Grading a room was a one-way door: the page held exactly
 * one league, and starting another one meant the first was gone. Somebody who
 * pastes their Yahoo draft, reads the room, then wants to look at their second
 * league had to choose between them. This is the list that ends that — every
 * graded room is filed as it is read, and any of them reopens instantly.
 *
 * 🔴 THIS IS THE THIRD STORAGE FEATURE ON THIS SURFACE AND ALL THREE ARE
 * DISTINCT KEYS, deliberately:
 *
 *   auctionArchitect_savedLeagues     the Team Builder's saved league SETTINGS
 *                                     (teams, budget, slots, scoring). Built in
 *                                     the builder, read here, never written here.
 *   auctionArchitect_rateTeam:<name>  the roster being TYPED, one slot per
 *                                     league (rateTeamStore.js). Work in
 *                                     progress, one league at a time. That file
 *                                     also owns RATE_UNLOCK_KEY, the email gate
 *                                     flag, which is a third question again.
 *   auctionArchitect_rateSavedRooms   this file: whole rooms already GRADED,
 *                                     many at once, each reopenable as it was.
 *
 * They answer different questions and merging any two of them would mean a rule
 * about which one wins. There is no such rule and there should not be.
 *
 * 🔴 NAMES ARE KEPT HERE, AND THAT IS THE POINT. Everything that LEAVES this
 * browser is seat-indexed and nameless (autoSubmitRoom.js says why at length).
 * This storage never leaves the device: it is the user's own league, read back
 * to the user, and a room report full of "Team 4" would be a worse product for
 * no privacy gain. The line to hold is the network boundary, not localStorage.
 *
 * 🔴 EVERY READ AND EVERY WRITE IS GUARDED AND A FAILURE IS SILENT, the same
 * rule rateTeamStore.js carries. Private mode, a full quota and a browser with
 * storage switched off all land on exactly the behaviour this page had before
 * saving existed: one room at a time, no picker, nothing said. A grading page
 * must never show anybody a storage error.
 */

import { roomContentId } from "./autoSubmitRoom.js";
import { leagueFormatLabel } from "./leagueFormat.js";

/** Whole graded rooms. Its own key — see the header for the other two. */
export const RATE_SAVED_ROOMS_KEY = "auctionArchitect_rateSavedRooms";

/**
 * How many rooms are kept, newest first.
 *
 * A cap exists because a room is a real amount of JSON (ten seats, sixteen rows
 * each) and localStorage is a few megabytes shared with everything else this
 * app stores. Twenty is far more leagues than anyone plays in and still leaves
 * the quota alone. Eviction is oldest-out by save time, never by use: a list
 * that reordered itself as it was read would move the chip somebody was aiming
 * at.
 */
export const SAVED_ROOMS_CAP = 20;

function isPlainObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/**
 * One stored room, or null.
 *
 * 🔴 A CORRUPT ROW IS NO ROW. The same rule the roster store has: a room that
 * cannot be read back whole is dropped rather than repaired, because a
 * half-restored room reopens as a league the user never entered and the report
 * grades it with a straight face.
 */
function readRoom(raw) {
  if (!isPlainObject(raw)) return null;
  if (typeof raw.id !== "string" || !raw.id) return null;
  if (!isPlainObject(raw.state) || !Array.isArray(raw.state.teams)) return null;
  const teams = raw.state.teams.filter((t) => isPlainObject(t) && isPlainObject(t.ladder));
  if (teams.length === 0) return null;
  return {
    id: raw.id,
    savedAt: Number(raw.savedAt) || 0,
    label: typeof raw.label === "string" && raw.label ? raw.label : "Your league",
    meta: isPlainObject(raw.meta) ? raw.meta : {},
    state: {
      savedName: typeof raw.state.savedName === "string" ? raw.state.savedName : null,
      manual: isPlainObject(raw.state.manual) ? raw.state.manual : null,
      teams: teams.map((t) => ({
        name: typeof t.name === "string" && t.name.trim() ? t.name : "Team",
        ladder: t.ladder,
      })),
      fromPaste: raw.state.fromPaste === true,
      proven: Array.isArray(raw.state.proven)
        ? raw.state.proven.filter((p) => typeof p === "string")
        : [],
    },
  };
}

/** Every kept room, newest first. A blocked or junk key is simply no rooms. */
export function readSavedRooms() {
  try {
    const raw = JSON.parse(localStorage.getItem(RATE_SAVED_ROOMS_KEY));
    if (!Array.isArray(raw)) return [];
    return raw
      .map(readRoom)
      .filter(Boolean)
      .sort((a, b) => b.savedAt - a.savedAt)
      .slice(0, SAVED_ROOMS_CAP);
  } catch {
    return [];
  }
}

function writeAll(rooms) {
  try {
    localStorage.setItem(RATE_SAVED_ROOMS_KEY, JSON.stringify(rooms));
  } catch {
    /* private mode, quota, storage disabled — the room on screen still works */
  }
}

/**
 * File a graded room, or update the one already filed under this content.
 *
 * 🔴 RE-GRADING IS AN UPDATE, NEVER A SECOND ENTRY. The id is the content hash
 * the corpus submit already dedupes on (roomContentId), so pasting the same
 * draft twice, or walking back to the receipt and confirming again, lands on
 * the same row. A picker that grew a chip every time somebody pressed the
 * button would be a list of one league.
 *
 * @returns {Array} the list as it now stands, newest first
 */
export function saveRoom(room) {
  const next = readRoom(room);
  if (!next) return readSavedRooms();
  const rest = readSavedRooms().filter((r) => r.id !== next.id);
  const all = [next, ...rest].slice(0, SAVED_ROOMS_CAP);
  writeAll(all);
  return all;
}

/** Forget one room. The chip's own remove affordance, and nothing else. */
export function removeSavedRoom(id) {
  const all = readSavedRooms().filter((r) => r.id !== id);
  writeAll(all);
  return all;
}

/**
 * What a saved room is called in the picker.
 *
 * The league's own name when there is one — which there is when the reader
 * picked a saved league for the lineup, and is not when they pasted a draft
 * with nothing but prices on it. The fallback describes the room rather than
 * numbering it, because "10-team sflex $200" tells somebody which of their
 * leagues this is and "League 3" tells them nothing.
 */
export function roomLabel(settings) {
  if (settings && settings.name) return settings.name;
  const teams = (settings && settings.teams) || 0;
  const budget = (settings && settings.budget) || 0;
  return `${teams}-team ${leagueFormatLabel(settings && settings.positions)} $${budget}`;
}

/**
 * The record for a room on screen: what the picker draws, and what TeamTab
 * needs to put itself back exactly as it was.
 *
 * `meta` is for the chip alone. `state` is TeamTab's own state, stored rather
 * than re-derived: the settings in force come from a saved league OR from the
 * manual form depending on which the reader used, and a store that kept only
 * the resolved settings would reopen a saved-league room as a manual one.
 */
export function roomRecord({
  settings,
  roomEntries,
  scoringPreset,
  savedName,
  manual,
  teams,
  fromPaste,
  proven,
}) {
  const id = roomContentId({ settings, teams: roomEntries, scoringPreset });
  if (!id) return null;
  return {
    id,
    savedAt: Date.now(),
    label: roomLabel(settings),
    meta: {
      teams: settings.teams,
      budget: settings.budget,
      format: leagueFormatLabel(settings.positions),
    },
    state: {
      savedName: savedName || null,
      manual: manual || null,
      teams,
      fromPaste: !!fromPaste,
      proven: proven || [],
    },
  };
}
